<script>
  import { onMount } from 'svelte';
  import { getApiBase } from './apiBase.js';

  const endpoint = `${getApiBase()}/studentplayground/api/admin/account-creation/students/`;
  const databaseLabels = {
    username: 'Username', first_name: 'First name', last_name: 'Last name',
    student_email: 'Student email', google_cal_searchable: 'Calendar name',
    grade: 'Grade', student_phone: 'Student phone', parent_name: 'Parent name',
    parent_email: 'Parent email', parent_phone: 'Parent phone',
    special_conditions: 'Special conditions',
  };
  const spreadsheetLabels = {
    student_name: 'Student name', grade: 'Grade', student_phone: 'Student phone',
    parent_phone: 'Parent phone', student_email: 'Student email',
    parent_name: 'Parent name', parent_email: 'Parent email',
    special_conditions: 'Special conditions',
  };

  let query = '';
  let rows = [];
  let page = 1;
  let hasMore = false;
  let loading = false;
  let error = '';
  let selectedId = null;
  let detail = null;
  let detailLoading = false;
  let resendOpen = false;
  let selectedRecipients = [];
  let resending = false;
  let feedback = '';
  let feedbackIsError = false;
  let searchTimer;
  let requestSequence = 0;
  let editing = false;
  let editForm = {};
  let saving = false;
  let deleteOpen = false;
  let deleteUsername = '';
  let deleting = false;
  let notice = '';
  const conditionOptions = ['Special Needs', 'IB', 'Lithuanian Second Language'];
  $: busy = saving || deleting || resending;

  function statusLabel(value) {
    return ({ complete: 'Complete', incomplete: 'Incomplete', success: 'Successful',
      failed: 'Failed', partial: 'Partial', sending: 'Sending', pending: 'Pending',
      unknown: 'Unknown', unavailable: 'Unavailable' })[value] || 'Unknown';
  }

  function authHeaders() {
    const token = localStorage.getItem('jwtToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async function responseBody(response) {
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const fields = body.fields ? Object.entries(body.fields).map(([key, messages]) => `${databaseLabels[key] || key}: ${messages.join(', ')}`).join(' ') : '';
      throw new Error(fields || body.error || body.detail || `Request failed (${response.status}).`);
    }
    return body;
  }

  async function loadList(nextPage = 1) {
    const sequence = ++requestSequence;
    loading = true;
    error = '';
    try {
      const params = new URLSearchParams({ page: String(nextPage) });
      if (query.trim()) params.set('q', query.trim());
      const data = await responseBody(await fetch(`${endpoint}?${params}`, { headers: authHeaders() }));
      if (sequence !== requestSequence) return;
      rows = data.results || [];
      page = data.page || nextPage;
      hasMore = !!data.has_more;
    } catch (cause) {
      if (sequence === requestSequence) error = cause.message;
    } finally {
      if (sequence === requestSequence) loading = false;
    }
  }

  function queueSearch() {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => loadList(1), 250);
  }

  async function selectStudent(id) {
    if (selectedId === id) {
      selectedId = null;
      detail = null;
      return;
    }
    selectedId = id;
    detail = null;
    detailLoading = true;
    feedback = '';
    resendOpen = false;
    editing = false;
    deleteOpen = false;
    notice = '';
    try {
      const data = await responseBody(await fetch(`${endpoint}${id}/`, { headers: authHeaders() }));
      if (selectedId === id) detail = data;
    } catch (cause) {
      if (selectedId === id) error = cause.message;
    } finally {
      if (selectedId === id) detailLoading = false;
    }
  }

  export async function refreshAndSelect(id) {
    query = '';
    await loadList(1);
    selectedId = null;
    await selectStudent(id);
  }

  function toggleRecipient(email) {
    selectedRecipients = selectedRecipients.includes(email)
      ? selectedRecipients.filter(item => item !== email)
      : [...selectedRecipients, email];
  }

  function applyDetail(updated) {
    detail = updated;
    rows = rows.map(row => row.id === updated.id ? {
      ...row, username: updated.username, display_name: updated.display_name,
      email_status: updated.email.status, spreadsheet_status: updated.spreadsheet.status,
      database_status: updated.database.status,
    } : row);
  }

  function beginEdit() {
    editForm = Object.fromEntries(Object.keys(databaseLabels).map(key => [key,
      key === 'special_conditions' ? [...(detail.database[key] || [])] : String(detail.database[key] || '')]));
    editing = true;
    resendOpen = false;
    deleteOpen = false;
    feedback = '';
  }

  async function saveAppData() {
    if (!detail || busy) return;
    saving = true;
    feedback = '';
    feedbackIsError = false;
    try {
      const updated = await responseBody(await fetch(`${endpoint}${detail.id}/`, {
        method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      }));
      applyDetail(updated);
      editing = false;
      feedback = 'App data saved.';
    } catch (cause) {
      feedback = cause.message;
      feedbackIsError = true;
    } finally {
      saving = false;
    }
  }

  async function deleteUser() {
    if (!detail || busy || deleteUsername !== detail.username) return;
    deleting = true;
    feedback = '';
    feedbackIsError = false;
    try {
      await responseBody(await fetch(`${endpoint}${detail.id}/`, {
        method: 'DELETE', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmation_username: deleteUsername }),
      }));
      rows = rows.filter(row => row.id !== detail.id);
      detail = null;
      selectedId = null;
      deleteOpen = false;
      await loadList(page);
      if (page > 1 && !rows.length) await loadList(page - 1);
      notice = 'User deleted.';
    } catch (cause) {
      feedback = cause.message;
      feedbackIsError = true;
    } finally {
      deleting = false;
    }
  }

  async function confirmResend() {
    if (!detail || !selectedRecipients.length || resending) return;
    resending = true;
    feedback = '';
    feedbackIsError = false;
    try {
      const updated = await responseBody(await fetch(`${endpoint}${detail.id}/resend/`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients: selectedRecipients }),
      }));
      applyDetail(updated);
      const accepted = (updated.resend_results || []).filter(item => item.success).map(item => item.email);
      const failed = (updated.resend_results || []).filter(item => !item.success).map(item => item.email);
      feedbackIsError = failed.length > 0;
      feedback = [
        ...accepted.map(email => `Email accepted for ${email}`),
        ...failed.map(email => `Email failed for ${email}`),
      ].join('. ');
      resendOpen = false;
      selectedRecipients = [];
    } catch (cause) {
      feedback = cause.message;
      feedbackIsError = true;
    } finally {
      resending = false;
    }
  }

  onMount(() => {
    loadList();
    return () => clearTimeout(searchTimer);
  });
</script>

<section class="creation-status" aria-label="Student creation status">
  <h3>Student creation status</h3>
  <label for="creation-status-search">Search student creation status</label>
  <input id="creation-status-search" type="search" bind:value={query} on:input={queueSearch}
    placeholder="Name, username, or email" disabled={busy} />
  {#if loading}<p role="status">Loading students…</p>{/if}
  {#if error}<p class="error" role="alert">{error}</p>{/if}
  {#if notice}<p role="status">{notice}</p>{/if}

  {#if !loading && !rows.length && !error}
    <p>No students found.</p>
  {/if}
  <ul class="students">
    {#each rows as row (row.id)}
      <li>
        <button type="button" class="student-row" aria-expanded={selectedId === row.id}
          disabled={busy} on:click={() => selectStudent(row.id)}>
          <strong>{row.display_name}</strong><span>@{row.username}</span>
          <small class:good={row.database_status === 'complete'} class:warn={row.database_status === 'incomplete'}>Database: {statusLabel(row.database_status)}</small>
          <small class:good={row.spreadsheet_status === 'success'} class:warn={row.spreadsheet_status === 'failed'}>Spreadsheet: {statusLabel(row.spreadsheet_status)}</small>
          <small class:good={row.email_status === 'success'} class:warn={row.email_status === 'failed' || row.email_status === 'partial'}>Email: {statusLabel(row.email_status)}</small>
        </button>
        {#if selectedId === row.id}
          {#if detailLoading}<p role="status">Loading account…</p>{/if}
          {#if detail}
            <div class="student-detail">
              <h4>Tutory App database <span>{statusLabel(detail.database.status)}</span></h4>
              {#if editing}
                <form class="edit-form" aria-label="Edit app data" on:submit|preventDefault={saveAppData}>
                  {#each Object.entries(databaseLabels).filter(([key]) => key !== 'special_conditions') as [key, label]}
                    <label>{label}
                      {#if key === 'grade'}
                        <input type="number" min="1" max="13" step="1" required bind:value={editForm[key]} disabled={saving} />
                      {:else if key === 'student_email' || key === 'parent_email'}
                        <input type="email" maxlength="254" required bind:value={editForm[key]} disabled={saving} />
                      {:else}
                        <input type="text" maxlength={key.includes('phone') ? 15 : key === 'google_cal_searchable' ? 50 : key === 'parent_name' ? 301 : 150}
                          required={key !== 'parent_phone'} bind:value={editForm[key]} disabled={saving} />
                      {/if}
                    </label>
                  {/each}
                  <fieldset disabled={saving}>
                    <legend>Special conditions</legend>
                    {#each conditionOptions as condition}
                      <label class="recipient"><input type="checkbox" value={condition} bind:group={editForm.special_conditions} /> {condition}</label>
                    {/each}
                  </fieldset>
                  <p>Changes update app data. The spreadsheet section keeps the creation snapshot.</p>
                  <div class="actions">
                    <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save app data'}</button>
                    <button type="button" disabled={saving} on:click={() => { editing = false; feedback = ''; }}>Cancel editing</button>
                  </div>
                  {#if feedback}<p class:error={feedbackIsError} class:success={!feedbackIsError} role={feedbackIsError ? 'alert' : 'status'}>{feedback}</p>{/if}
                </form>
              {:else}
              <dl>
                {#each Object.entries(databaseLabels) as [key, label]}
                  <dt>{label}</dt><dd>{Array.isArray(detail.database[key]) ? (detail.database[key].join(', ') || 'None') : (detail.database[key] || 'Missing')}</dd>
                {/each}
              </dl>
              <button type="button" disabled={busy} on:click={beginEdit}>Edit app data</button>
              {/if}
              {#if !editing && feedback}<p class:error={feedbackIsError} class:success={!feedbackIsError} role={feedbackIsError ? 'alert' : 'status'}>{feedback}</p>{/if}
              <h4>Student spreadsheet <span>{statusLabel(detail.spreadsheet.status)}</span></h4>
              <dl>
                {#each Object.entries(spreadsheetLabels) as [key, label]}
                  <dt>{label}</dt><dd>{Array.isArray(detail.spreadsheet.fields[key]) ? (detail.spreadsheet.fields[key].join(', ') || 'None') : (detail.spreadsheet.fields[key] || 'Blank')}</dd>
                {/each}
              </dl>
              <h4>Creation email <span>{statusLabel(detail.email.status)}</span></h4>
              {#each detail.email.recipients as recipient}
                <p>{recipient.kind}: {recipient.email} — {statusLabel(recipient.status)}{recipient.source === 'legacy_assumed' ? ' (legacy default)' : ''}</p>
              {/each}
              {#if detail.can_resend}
                <button type="button" disabled={busy || editing} on:click={() => { resendOpen = true; deleteOpen = false; selectedRecipients = []; feedback = ''; }}>
                  Resend creation email
                </button>
              {:else}
                <p>Resend unavailable. Use password reset if the initial password has changed.</p>
              {/if}
              {#if resendOpen}
                <div class="resend-dialog" role="dialog" aria-modal="true" aria-label="Choose creation email recipients">
                  <h5>Choose recipients</h5>
                  {#each detail.email.recipients as recipient}
                    <label class="recipient"><input type="checkbox" checked={selectedRecipients.includes(recipient.email)}
                      on:change={() => toggleRecipient(recipient.email)} /> {recipient.email}</label>
                  {/each}
                  <p>This sends the account creation email with the initial credentials.</p>
                  <button type="button" disabled={!selectedRecipients.length || resending} on:click={confirmResend}>
                    {resending ? 'Sending…' : 'Confirm resend'}
                  </button>
                  <button type="button" disabled={resending} on:click={() => { resendOpen = false; selectedRecipients = []; }}>Cancel</button>
                </div>
              {/if}
              <div class="delete-section">
                <button type="button" class="danger" disabled={busy || editing}
                  on:click={() => { deleteOpen = true; resendOpen = false; deleteUsername = ''; feedback = ''; }}>Delete user</button>
                {#if deleteOpen}
                  <div class="delete-confirmation" role="group" aria-label="Confirm user deletion">
                    <p>Permanently delete @{detail.username} and related app records, including lesson, payment and email history? The spreadsheet entry stays in the spreadsheet.</p>
                    <label>Type username to confirm deletion
                      <input type="text" autocomplete="off" bind:value={deleteUsername} disabled={deleting} />
                    </label>
                    <div class="actions">
                      <button type="button" class="danger" disabled={deleteUsername !== detail.username || busy} on:click={deleteUser}>
                        {deleting ? 'Deleting…' : 'Confirm delete'}
                      </button>
                      <button type="button" disabled={deleting} on:click={() => { deleteOpen = false; deleteUsername = ''; }}>Cancel deletion</button>
                    </div>
                  </div>
                {/if}
              </div>
            </div>
          {/if}
        {/if}
      </li>
    {/each}
  </ul>
  <div class="pages">
    <button type="button" disabled={page <= 1 || loading || busy} on:click={() => loadList(page - 1)}>Previous</button>
    <span>Page {page}</span>
    <button type="button" disabled={!hasMore || loading || busy} on:click={() => loadList(page + 1)}>Next</button>
  </div>
</section>

<style>
  .creation-status { margin-top: 2rem; padding-top: 1.25rem; border-top: 1px solid #cbd5e1; }
  .creation-status h3 { margin: 0 0 .75rem; }
  .creation-status > label { display: block; font-weight: 600; margin-bottom: .4rem; }
  .creation-status > input { box-sizing: border-box; width: 100%; padding: .6rem; border: 1px solid #94a3b8; border-radius: 4px; }
  .students { padding: 0; list-style: none; }
  .students li { border-bottom: 1px solid #e2e8f0; }
  .student-row { display: flex; flex-wrap: wrap; gap: .35rem .7rem; width: 100%; text-align: left; padding: .7rem .3rem; background: transparent; border: 0; cursor: pointer; }
  .student-row strong { width: 100%; }
  .student-row span { color: #475569; }
  .student-row small { padding: .15rem .3rem; background: #f1f5f9; border-radius: 3px; }
  .student-row small.good { background: #dcfce7; color: #166534; }
  .student-row small.warn { background: #fee2e2; color: #991b1b; }
  .student-detail { padding: .8rem; background: #f8fafc; border-radius: 4px; }
  .student-detail h4 { margin: 1rem 0 .45rem; }
  .student-detail h4 span { font-weight: normal; }
  dl { display: grid; grid-template-columns: minmax(8rem, 1fr) 2fr; gap: .35rem .6rem; margin: .4rem 0 1rem; overflow-wrap: anywhere; }
  dt { font-weight: 600; } dd { margin: 0; }
  .resend-dialog { margin-top: .7rem; padding: .8rem; border: 1px solid #94a3b8; background: white; }
  .recipient { display: block; margin: .45rem 0; }
  .pages { display: flex; align-items: center; gap: .7rem; }
  .error { color: #b91c1c; }
  .success { color: #166534; }
  .edit-form > label, .delete-confirmation > label { display: block; margin: .65rem 0; font-weight: 600; }
  .edit-form > label input, .delete-confirmation input { display: block; width: 100%; box-sizing: border-box; padding: .5rem; margin-top: .3rem; }
  .actions { display: flex; flex-wrap: wrap; gap: .5rem; margin-top: .7rem; }
  .delete-section { border-top: 1px solid #cbd5e1; margin-top: 1.5rem; padding-top: 1rem; }
  .danger { color: #991b1b; border: 1px solid #991b1b; border-radius: 4px; padding: .5rem .7rem; background: white; }
  button { cursor: pointer; }
  button:disabled { cursor: default; opacity: .55; }
</style>
